import { useEffect } from 'react';

import { useMyMembershipsQuery } from './api';
import type { OwnMembership } from './types';

import { useSession } from '@/lib/session';

export interface UseActiveShopResult {
  readonly memberships: readonly OwnMembership[];
  readonly isLoading: boolean;
  readonly activeShopId: string | null;
  readonly activeMembership: OwnMembership | null;
  readonly selectShop: (tenantId: string) => void;
}

/**
 * Resolves which shop the merchant-scoped hooks (features/products,
 * features/inventory) act on.
 *
 * Auto-selects the first membership with `membershipStatus === 'active'`
 * the first time the list loads and nothing is selected yet — mirrors
 * OQ-16's own "if a person has exactly one shop" convenience case without
 * silently re-selecting on every render (the effect only fires while
 * `activeShopId` is still null). A membership whose `membershipStatus` is
 * `'suspended'` is listed (see `TenantMembershipProbe.listOwn()`'s own
 * reasoning) but never auto-selected — it could not bind `X-Shop-Id`
 * successfully even if it were (`TenantResolutionGuard` checks the SAME
 * `'active'` status independently, server-side).
 */
export function useActiveShop(): UseActiveShopResult {
  const { activeShopId, setActiveShop } = useSession();
  const { data, isLoading } = useMyMembershipsQuery();
  const memberships = data ?? [];

  useEffect(() => {
    if (activeShopId !== null) return;
    const firstActive = memberships.find((m) => m.membershipStatus === 'active');
    if (firstActive !== undefined) setActiveShop(firstActive.tenantId);
  }, [activeShopId, memberships, setActiveShop]);

  const activeMembership = memberships.find((m) => m.tenantId === activeShopId) ?? null;

  return {
    memberships,
    isLoading,
    activeShopId,
    activeMembership,
    selectShop: setActiveShop,
  };
}
